import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  listPublicSystemAgentCategories,
  listPublicSystemAgents,
  type SystemAgentCategoryItem,
} from "@thyra/client";
import { ArrowRight, RefreshCw, WifiOff } from "lucide-react";
import { useTranslation } from "@thyra/landing-i18n";
import { getAppUrl } from "../env";
import { toCatalogLocale } from "../lib/catalog-locale";
import {
  buildCategories,
  filterByCategory,
  isTemplateId,
  normalizeAgents,
  type CatalogAgent,
} from "../lib/agent-catalog";
import { getAgentsItemListSchema } from "../seo/schema";
import { AgentCard, AgentCardSkeleton } from "./agents/AgentCard";
import { AgentCarousel, CAROUSEL_ITEM_CLASS } from "./agents/AgentCarousel";
import {
  AgentCategoryChips,
  AgentCategoryChipsSkeleton,
} from "./agents/AgentCategoryChips";
import { SectionHeader } from "./ui/SectionHeader";

/**
 * "Pick your assistant": the whole public catalog of ready-to-use agents (GET /api/public/system-agents),
 * filterable by category and browsed in a two-up carousel. If the API is unreachable the section says so, offers a
 * retry, and shows a short preview whose links go to the app's gallery, never to an invented template id.
 */

type Status = "loading" | "ready" | "error";

/** Preview used only when the API is unreachable. Copy lives in assistantRoles.fallbackAgents.*. */
const FALLBACK_AGENTS = [
  { id: "personalAssistant", iconKey: "BotMessageSquare", asks: true },
  { id: "smartInboxAssistant", iconKey: "Inbox", asks: true },
  { id: "opsScheduler", iconKey: "CalendarClock", asks: true },
  { id: "researchBriefWriter", iconKey: "Microscope", asks: false },
  { id: "supportInboxAssistant", iconKey: "Headphones", asks: true },
  { id: "emailUnsubscribeManager", iconKey: "MailX", asks: true },
] as const;

function galleryHref(): string {
  return `${getAppUrl()}/create-agent`;
}

function templateHref(agentId: string): string {
  return isTemplateId(agentId)
    ? `${getAppUrl()}/welcome/from-template?template=${encodeURIComponent(agentId)}`
    : galleryHref();
}

export function AssistantRolesSection() {
  const { t, i18n } = useTranslation("sections");
  const catalogLocale = toCatalogLocale(i18n.language);

  const [status, setStatus] = useState<Status>("loading");
  const [agents, setAgents] = useState<CatalogAgent[]>([]);
  const [rawCategories, setRawCategories] = useState<SystemAgentCategoryItem[]>(
    [],
  );
  const [attempt, setAttempt] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    async function load() {
      try {
        const [agentsRes, categoriesRes] = await Promise.all([
          listPublicSystemAgents({
            query: { locale: catalogLocale },
            throwOnError: true,
          }),
          // Chips are an enhancement: without them the full grid still works.
          listPublicSystemAgentCategories({
            query: { locale: catalogLocale },
            throwOnError: true,
          }).catch(() => null),
        ]);
        if (cancelled) return;
        const list = normalizeAgents(agentsRes.data);
        if (list.length === 0) throw new Error("empty catalog");
        setAgents(list);
        setRawCategories(
          Array.isArray(categoriesRes?.data) ? categoriesRes.data : [],
        );
        setStatus("ready");
      } catch (error) {
        if (cancelled) return;
        // The visitor sees the error state; the cause is only useful to whoever is developing.
        // DEV is a Vite built-in, not a turbo-managed env var.
        // eslint-disable-next-line turbo/no-undeclared-env-vars
        if (import.meta.env.DEV) console.error("Agent catalog failed", error);
        setAgents([]);
        setRawCategories([]);
        setStatus("error");
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [catalogLocale, attempt]);

  const categories = useMemo(
    () => buildCategories(rawCategories, agents),
    [rawCategories, agents],
  );
  const categoryLabels = useMemo(
    () => new Map(categories.map((c) => [c.slug, c.label])),
    [categories],
  );
  // A category that disappears after a reload (locale change) must not leave an empty list.
  const activeSlug =
    selected && categories.some((c) => c.slug === selected) ? selected : null;
  const filtered = useMemo(
    () => filterByCategory(agents, activeSlug),
    [agents, activeSlug],
  );

  const fallbackAgents: CatalogAgent[] = FALLBACK_AGENTS.map((f) => ({
    id: f.id,
    name: t(`assistantRoles.fallbackAgents.${f.id}.name`),
    description: t(`assistantRoles.fallbackAgents.${f.id}.description`),
    iconKey: f.iconKey,
    category: null,
    highlights: [],
    requiresApproval: f.asks,
    cadence: null,
    connections: [],
    optionalConnections: [],
    tools: [],
    guardrails: 0,
    instructions: 0,
    memory: null,
    knowledgeBase: false,
  }));

  const itemListSchema = useMemo(
    () =>
      status === "ready"
        ? getAgentsItemListSchema(
            t("assistantRoles.title"),
            agents.map((a) => ({
              name: a.name,
              description: a.description,
            })),
          )
        : null,
    [status, agents, t],
  );

  const announce =
    status === "ready"
      ? t("assistantRoles.resultsAnnouncement", {
          category: activeSlug
            ? (categoryLabels.get(activeSlug) ?? "")
            : t("assistantRoles.allCategories"),
          count: filtered.length,
        })
      : "";

  return (
    <section className="overflow-hidden py-20 sm:py-28">
      {itemListSchema ? (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(itemListSchema)}
          </script>
        </Helmet>
      ) : null}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow={t("assistantRoles.eyebrow")}
          title={t("assistantRoles.title")}
          description={t("assistantRoles.description")}
        />

        <div className="mt-10 min-h-[2.75rem]">
          {status === "loading" ? <AgentCategoryChipsSkeleton /> : null}
          {status === "ready" && categories.length > 0 ? (
            <AgentCategoryChips
              categories={categories}
              totalCount={agents.length}
              selected={activeSlug}
              onSelect={setSelected}
            />
          ) : null}
        </div>

        <p role="status" aria-live="polite" className="sr-only">
          {announce}
        </p>

        {status === "error" ? (
          <div
            role="alert"
            className="marketing-card mt-8 flex flex-col items-start gap-4 p-5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground-strong">
                <WifiOff className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <p className="text-sm font-semibold text-card-foreground">
                  {t("assistantRoles.error.title")}
                </p>
                <p className="mt-1 text-sm text-muted-foreground-strong">
                  {t("assistantRoles.error.body")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAttempt((n) => n + 1)}
              className="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
              {t("assistantRoles.error.retry")}
            </button>
          </div>
        ) : null}

        {status === "loading" ? (
          <ul
            aria-busy
            aria-label={t("assistantRoles.loading")}
            className="mt-8 flex gap-5"
          >
            <AgentCardSkeleton className={CAROUSEL_ITEM_CLASS} />
            <AgentCardSkeleton
              className={`${CAROUSEL_ITEM_CLASS} hidden md:flex`}
            />
          </ul>
        ) : null}
        {status === "ready" ? (
          <AgentCarousel count={filtered.length} resetKey={activeSlug}>
            {filtered.map((agent, i) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                categoryLabel={
                  agent.category
                    ? categoryLabels.get(agent.category)
                    : undefined
                }
                href={templateHref(agent.id)}
                ctaLabel={t("assistantRoles.startCta")}
                delayMs={(i % 2) * 60}
                className={CAROUSEL_ITEM_CLASS}
              />
            ))}
          </AgentCarousel>
        ) : null}
        {status === "error" ? (
          <AgentCarousel count={fallbackAgents.length} resetKey="fallback">
            {fallbackAgents.map((agent, i) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                href={galleryHref()}
                ctaLabel={t("assistantRoles.fallbackCta")}
                delayMs={(i % 2) * 60}
                hideAccounts
                className={CAROUSEL_ITEM_CLASS}
              />
            ))}
          </AgentCarousel>
        ) : null}

        <div className="mt-10 flex flex-col items-center gap-5">
          <a
            href={galleryHref()}
            rel="nofollow"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {t("assistantRoles.browseCatalog")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </a>
        </div>
      </div>
    </section>
  );
}
